import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../utils/version';
import { formatErrorMessage, showErrorMessage } from '../utils/errorHandler';

function Settings() {
  const { user } = useAuth();
  const [slackSettings, setSlackSettings] = useState({
    webhook_url: '',
    channel_name: '',
    is_active: false
  });
  const [googleDriveSettings, setGoogleDriveSettings] = useState({
    medical_certificates_folder_id: '',
    compensation_approval_attachments_folder_id: '',
    other_paid_leave_documents_folder_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [savingSlack, setSavingSlack] = useState(false);
  const [savingGoogleDrive, setSavingGoogleDrive] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (user?.is_admin) {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Fetch Slack settings
      const slackResponse = await api.get('/slack/settings');
      if (slackResponse.data) {
        setSlackSettings({
          webhook_url: slackResponse.data.webhook_url || '',
          channel_name: slackResponse.data.channel_name || '',
          is_active: !!slackResponse.data.is_active
        });
      }
      // Fetch Google Drive settings
      const googleDriveResponse = await api.get('/settings/google-drive');
      if (googleDriveResponse.data) {
        const d = googleDriveResponse.data;
        setGoogleDriveSettings({
          medical_certificates_folder_id:
            d.medical_certificates_folder_id || d.folder_id || '',
          compensation_approval_attachments_folder_id:
            d.compensation_approval_attachments_folder_id || '',
          other_paid_leave_documents_folder_id:
            d.other_paid_leave_documents_folder_id || ''
        });
      }
    } catch (error) {
      console.error('Settings fetch error:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSlackChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSlackSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGoogleDriveChange = (e) => {
    const { name, value } = e.target;
    setGoogleDriveSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSlackSubmit = async (e) => {
    e.preventDefault();
    
    if (!slackSettings.webhook_url || !slackSettings.channel_name) {
      toast.error('Webhook URL and channel name are required');
      return;
    }

    try {
      setSavingSlack(true);
      await api.post('/slack/settings', slackSettings);
      toast.success('Slack settings saved successfully');
    } catch (error) {
      console.error('Slack settings save error:', error);
      const message = formatErrorMessage(error);
      showErrorMessage(toast.error, message);
    } finally {
      setSavingSlack(false);
    }
  };

  const handleGoogleDriveSubmit = async (e) => {
    e.preventDefault();

    try {
      setSavingGoogleDrive(true);
      await api.post('/settings/google-drive', googleDriveSettings);
      toast.success('Google Drive settings saved successfully');
    } catch (error) {
      console.error('Google Drive settings save error:', error);
      const message = formatErrorMessage(error);
      showErrorMessage(toast.error, message);
    } finally {
      setSavingGoogleDrive(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      await api.post('/slack/test');
      toast.success('Slack test message sent successfully');
    } catch (error) {
      console.error('Slack test error:', error);
      const message = formatErrorMessage(error);
      showErrorMessage(toast.error, message);
    } finally {
      setTesting(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="card">
        <h3>Access Denied</h3>
        <p>This page is only accessible to administrators.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Settings</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
      </div>

      {/* Slack Integration Settings Section */}
      <div className="card mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Slack Integration Settings</h2>
          <div className="slack-status">
            <span className={`badge ${slackSettings.is_active ? 'badge-success' : 'badge-secondary'}`}>
              {slackSettings.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="slack-info mb-4">
          <h3>📱 About Slack Integration</h3>
          <p>When a leave application is submitted, notifications are sent to the specified Slack channel.</p>
          
          <div className="setup-steps">
            <h4>🔧 Setup Instructions</h4>
            <ol>
              <li>Add <strong>Incoming Webhook App</strong> to your Slack workspace</li>
              <li>Select the channel where you want to send notifications</li>
              <li>Enter the generated Webhook URL in the field below</li>
              <li>After saving settings, use test send to verify functionality</li>
            </ol>
          </div>

          <div className="notification-format">
            <h4>📄 Notification Content</h4>
            <p>The following information will be notified:</p>
            <ul>
              <li>Applicant name and department</li>
              <li>Leave type</li>
              <li>Period (start date ~ end date)</li>
              <li>Approver</li>
              <li>Reason for application (if entered)</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSlackSubmit}>
          <div className="form-group">
            <label className="form-label">Webhook URL *</label>
            <input
              type="url"
              name="webhook_url"
              className="form-control"
              value={slackSettings.webhook_url}
              onChange={handleSlackChange}
              placeholder="https://hooks.slack.com/services/YOUR/SLACK/WEBHOOK"
              required
            />
            <small className="form-text text-muted">
              Please enter your Slack Incoming Webhook URL
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Channel Name *</label>
            <input
              type="text"
              name="channel_name"
              className="form-control"
              value={slackSettings.channel_name}
              onChange={handleSlackChange}
              placeholder="#attendance-approval"
              required
            />
            <small className="form-text text-muted">
              Slack channel name to send notifications to (include #)
            </small>
          </div>

          <div className="form-group">
            <div className="slack-enable-row">
              <div className="slack-enable-labels">
                <span className="form-label" id="slack-is-active-label">
                  Enable Slack notifications
                </span>
                <small className="form-text text-muted slack-enable-hint">
                  When enabled, new leave applications are posted to the Slack channel using the
                  webhook above.
                </small>
              </div>
              <label className="settings-toggle" htmlFor="slack-is-active" title="Enable Slack">
                <input
                  id="slack-is-active"
                  type="checkbox"
                  name="is_active"
                  className="settings-toggle-input"
                  checked={slackSettings.is_active}
                  onChange={handleSlackChange}
                  aria-labelledby="slack-is-active-label"
                />
                <span className="settings-toggle-track" aria-hidden="true" />
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleTest}
              disabled={testing || !slackSettings.webhook_url || !slackSettings.channel_name}
            >
              {testing ? 'Sending test...' : '🧪 Test Send'}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingSlack}
            >
              {savingSlack ? 'Saving...' : '💾 Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Google Drive Settings Section */}
      <div className="card mb-4">
        <h2>Google Drive Settings</h2>
        
        <div className="google-drive-info mb-4">
          <h3>📁 About Google Drive Integration</h3>
          <p>
            Configure Google Drive folder IDs for medical certificate uploads, compensation
            approval attachments, and other paid leave supporting documents.
          </p>
        </div>

        <form onSubmit={handleGoogleDriveSubmit}>
          <div className="form-group">
            <label className="form-label">Medical certificates Google Drive Folder ID</label>
            <input
              type="text"
              name="medical_certificates_folder_id"
              className="form-control"
              value={googleDriveSettings.medical_certificates_folder_id}
              onChange={handleGoogleDriveChange}
              placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
            />
            <small className="form-text text-muted">
              Folder for sick leave / medical certificate uploads
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Compensation approval attachments Google Drive Folder ID
            </label>
            <input
              type="text"
              name="compensation_approval_attachments_folder_id"
              className="form-control"
              value={googleDriveSettings.compensation_approval_attachments_folder_id}
              onChange={handleGoogleDriveChange}
              placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
            />
            <small className="form-text text-muted">
              Folder for compensation approval attachment uploads
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Other paid leave documents Google Drive Folder ID
            </label>
            <input
              type="text"
              name="other_paid_leave_documents_folder_id"
              className="form-control"
              value={googleDriveSettings.other_paid_leave_documents_folder_id}
              onChange={handleGoogleDriveChange}
              placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
            />
            <small className="form-text text-muted">
              Folder for other paid leave supporting document uploads (other-paid-leave-documents)
            </small>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingGoogleDrive}
            >
              {savingGoogleDrive ? 'Saving...' : '💾 Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Troubleshooting Section */}
      <div className="card mt-4">
        <h3>💡 Troubleshooting</h3>
        <div className="troubleshooting">
          <div className="trouble-item">
            <h5>❌ When Webhook errors occur</h5>
            <ul>
              <li>Verify that the Webhook URL is configured correctly</li>
              <li>Check the permission settings of the Slack app</li>
              <li>Verify that the app has been added to the channel</li>
            </ul>
          </div>

          <div className="trouble-item">
            <h5>📞 When notifications are not received</h5>
            <ul>
              <li>Verify that settings are "Active"</li>
              <li>Run connection test with test send</li>
              <li>Check Slack channel settings</li>
            </ul>
          </div>

          <div className="trouble-item">
            <h5>⚡ About Performance</h5>
            <ul>
              <li>Slack notifications are sent asynchronously, so they don't affect application processing</li>
              <li>Application processing completes normally even during communication errors</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .slack-status {
          font-size: 1.1rem;
        }
        
        .slack-info,
        .google-drive-info {
          background-color: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          border-left: 4px solid #007bff;
        }
        
        .setup-steps {
          margin-top: 1.5rem;
        }
        
        .setup-steps ol {
          padding-left: 1.5rem;
        }
        
        .setup-steps li {
          margin-bottom: 0.5rem;
        }
        
        .notification-format {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #dee2e6;
        }
        
        .notification-format ul {
          padding-left: 1.5rem;
        }
        
        .notification-format li {
          margin-bottom: 0.25rem;
        }
        
        .slack-enable-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.25rem;
          padding: 1rem 1.25rem;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background: #fafbfc;
        }
        
        .slack-enable-labels {
          flex: 1;
          min-width: 0;
        }
        
        .slack-enable-labels .form-label {
          margin-bottom: 0.35rem;
        }
        
        .slack-enable-hint {
          display: block;
          line-height: 1.45;
          max-width: 42rem;
        }
        
        .settings-toggle {
          position: relative;
          display: inline-flex;
          flex-shrink: 0;
          cursor: pointer;
        }
        
        .settings-toggle-input {
          position: absolute;
          opacity: 0;
          width: 1.25rem;
          height: 1.25rem;
          margin: 0;
          cursor: pointer;
        }
        
        .settings-toggle-track {
          display: block;
          width: 3.25rem;
          height: 1.75rem;
          border-radius: 999px;
          background: #ced4da;
          transition: background-color 0.2s ease, box-shadow 0.2s ease;
          position: relative;
        }
        
        .settings-toggle-track::after {
          content: "";
          position: absolute;
          top: 0.2rem;
          left: 0.22rem;
          width: 1.35rem;
          height: 1.35rem;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          transition: transform 0.2s ease;
        }
        
        .settings-toggle-input:checked + .settings-toggle-track {
          background: #007bff;
        }
        
        .settings-toggle-input:checked + .settings-toggle-track::after {
          transform: translateX(1.4rem);
        }
        
        .settings-toggle-input:focus-visible + .settings-toggle-track {
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.35);
        }
        
        @media (max-width: 576px) {
          .slack-enable-row {
            flex-direction: column;
            align-items: stretch;
          }
          
          .settings-toggle {
            align-self: flex-start;
            margin-top: 0.5rem;
          }
        }
        
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #dee2e6;
        }
        
        .troubleshooting {
          display: grid;
          gap: 1.5rem;
        }
        
        .trouble-item {
          padding: 1rem;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          background-color: #fdfdfd;
        }
        
        .trouble-item h5 {
          margin-bottom: 0.75rem;
          color: #495057;
        }
        
        .trouble-item ul {
          margin-bottom: 0;
          padding-left: 1.25rem;
        }
        
        .trouble-item li {
          margin-bottom: 0.25rem;
          color: #6c757d;
        }
        
        @media (max-width: 768px) {
          .form-actions {
            flex-direction: column;
          }
          
          .setup-steps ol,
          .notification-format ul,
          .trouble-item ul {
            padding-left: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default Settings;





